/** @module @liquidefy/code-editor/terminal/terminal-multiplexer @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  MultiplexerState,
  MultiplexerType,
  MultiplexerConfig,
  MultiplexSession,
  MultiplexWindow,
  MultiplexPane,
  MultiplexSessionId,
  MultiplexWindowId,
  MultiplexPaneId,
  TerminalId,
  PaneLayout,
  PanePosition,
  PaneSize,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asMultiplexSessionId,
  asMultiplexWindowId,
  asMultiplexPaneId,
  asTerminalId,
  clamp,
  now,
  DEFAULT_MULTIPLEXER_CONFIG,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 MULTIPLEXER LIFECYCLE — createMultiplexer, configureMultiplexer, resetMultiplexer
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new multiplexer state.
 */
export function createMultiplexer(
  type: MultiplexerType,
  config?: Partial<MultiplexerConfig>
): MultiplexerState {
  return {
    type,
    sessions: [],
    activeSessionId: undefined,
    config: {
      ...DEFAULT_MULTIPLEXER_CONFIG,
      ...config,
    },
  };
}

/**
 * Update multiplexer configuration.
 */
export function configureMultiplexer(
  state: MultiplexerState,
  config: Partial<MultiplexerConfig>
): MultiplexerState {
  return {
    ...state,
    config: { ...state.config, ...config },
  };
}

/**
 * Reset multiplexer to initial state, removing all sessions.
 */
export function resetMultiplexer(state: MultiplexerState): MultiplexerState {
  return {
    ...state,
    sessions: [],
    activeSessionId: undefined,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 SESSION MANAGEMENT — createSession, removeSession, rename, attach, detach, navigate
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new multiplexer session.
 */
export function createSession(
  state: MultiplexerState,
  name: string
): EditorResult<MultiplexerState> {
  const sessionId = asMultiplexSessionId(generateId('mux-session'));
  const session: MultiplexSession = {
    id: sessionId,
    name,
    windows: [],
    activeWindowIndex: -1,
    isAttached: false,
    createdAt: now(),
  };

  const updated: MultiplexerState = {
    ...state,
    sessions: [...state.sessions, session],
  };
  return ok(updated, 'MUX_SESSION_CREATED', `Session '${name}' created`);
}

/**
 * Remove a multiplexer session by ID.
 */
export function removeSession(
  state: MultiplexerState,
  sessionId: MultiplexSessionId
): EditorResult<MultiplexerState> {
  const idx = state.sessions.findIndex(s => s.id === sessionId);
  if (idx < 0) {
    return err('MUX_SESSION_NOT_FOUND', `Session '${sessionId}' not found`);
  }

  const sessions = state.sessions.filter(s => s.id !== sessionId);
  const activeSessionId = state.activeSessionId === sessionId
    ? (sessions.length > 0 ? sessions[0]!.id : undefined)
    : state.activeSessionId;

  return ok(
    { ...state, sessions, activeSessionId },
    'MUX_SESSION_REMOVED',
    `Session '${sessionId}' removed`
  );
}

/**
 * Rename a multiplexer session.
 */
export function renameSession(
  state: MultiplexerState,
  sessionId: MultiplexSessionId,
  name: string
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(s =>
    s.id === sessionId ? { ...s, name } : s
  );
  if (!state.sessions.some(s => s.id === sessionId)) {
    return err('MUX_SESSION_NOT_FOUND', `Session '${sessionId}' not found`);
  }
  return ok({ ...state, sessions }, 'MUX_SESSION_RENAMED', `Session renamed to '${name}'`);
}

/**
 * Attach to a session.
 */
export function attachSession(
  state: MultiplexerState,
  sessionId: MultiplexSessionId
): EditorResult<MultiplexerState> {
  const idx = state.sessions.findIndex(s => s.id === sessionId);
  if (idx < 0) {
    return err('MUX_SESSION_NOT_FOUND', `Session '${sessionId}' not found`);
  }

  // Detach any currently attached session first
  const sessions = state.sessions.map(s => ({
    ...s,
    isAttached: s.id === sessionId,
  }));

  return ok(
    { ...state, sessions, activeSessionId: sessionId },
    'MUX_SESSION_ATTACHED',
    `Attached to session '${sessionId}'`
  );
}

/**
 * Detach from the current session.
 */
export function detachSession(
  state: MultiplexerState
): EditorResult<MultiplexerState> {
  if (!state.activeSessionId) {
    return err('MUX_NO_ACTIVE_SESSION', 'No active session to detach from');
  }

  const sessions = state.sessions.map(s =>
    s.id === state.activeSessionId ? { ...s, isAttached: false } : s
  );

  return ok(
    { ...state, sessions, activeSessionId: undefined },
    'MUX_SESSION_DETACHED',
    'Session detached'
  );
}

/**
 * Switch to the next session.
 */
export function nextSession(state: MultiplexerState): EditorResult<MultiplexerState> {
  if (state.sessions.length === 0) {
    return err('MUX_NO_SESSIONS', 'No sessions available');
  }
  const currentIdx = state.sessions.findIndex(s => s.id === state.activeSessionId);
  const nextIdx = (currentIdx + 1) % state.sessions.length;
  return attachSession(state, state.sessions[nextIdx]!.id);
}

/**
 * Switch to the previous session.
 */
export function previousSession(state: MultiplexerState): EditorResult<MultiplexerState> {
  if (state.sessions.length === 0) {
    return err('MUX_NO_SESSIONS', 'No sessions available');
  }
  const currentIdx = state.sessions.findIndex(s => s.id === state.activeSessionId);
  const prevIdx = currentIdx <= 0 ? state.sessions.length - 1 : currentIdx - 1;
  return attachSession(state, state.sessions[prevIdx]!.id);
}

/**
 * Switch to a session by index.
 */
export function switchToSession(
  state: MultiplexerState,
  index: number
): EditorResult<MultiplexerState> {
  if (index < 0 || index >= state.sessions.length) {
    return err('MUX_SESSION_INDEX_OUT_OF_RANGE', `Session index ${index} out of range`);
  }
  return attachSession(state, state.sessions[index]!.id);
}

/**
 * List all sessions.
 */
export function listSessions(state: MultiplexerState): MultiplexSession[] {
  return state.sessions;
}

/**
 * Get the active session.
 */
export function getActiveSession(state: MultiplexerState): MultiplexSession | undefined {
  return state.sessions.find(s => s.id === state.activeSessionId);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 WINDOW MANAGEMENT — createWindow, removeWindow, rename, navigate, move, list
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new window in a session.
 */
export function createWindow(
  state: MultiplexerState,
  sessionId: MultiplexSessionId,
  name: string
): EditorResult<MultiplexerState> {
  const sessionIdx = state.sessions.findIndex(s => s.id === sessionId);
  if (sessionIdx < 0) {
    return err('MUX_SESSION_NOT_FOUND', `Session '${sessionId}' not found`);
  }

  const windowId = asMultiplexWindowId(generateId('mux-window'));
  const window: MultiplexWindow = {
    id: windowId,
    name,
    panes: [],
    activePaneId: asMultiplexPaneId(''),
    layout: 'tiled',
  };

  const session = state.sessions[sessionIdx]!;
  const updatedSession: MultiplexSession = {
    ...session,
    windows: [...session.windows, window],
    activeWindowIndex: session.windows.length,
  };

  const sessions = [...state.sessions];
  sessions[sessionIdx] = updatedSession;

  return ok({ ...state, sessions }, 'MUX_WINDOW_CREATED', `Window '${name}' created`);
}

/**
 * Remove a window by ID.
 */
export function removeWindow(
  state: MultiplexerState,
  windowId: MultiplexWindowId
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(session => {
    const idx = session.windows.findIndex(w => w.id === windowId);
    if (idx < 0) return session;

    const windows = session.windows.filter(w => w.id !== windowId);
    const activeWindowIndex = clamp(
      session.activeWindowIndex >= windows.length ? windows.length - 1 : session.activeWindowIndex,
      0,
      Math.max(0, windows.length - 1)
    );

    return { ...session, windows, activeWindowIndex };
  });

  return ok({ ...state, sessions }, 'MUX_WINDOW_REMOVED', `Window '${windowId}' removed`);
}

/**
 * Rename a window.
 */
export function renameWindow(
  state: MultiplexerState,
  windowId: MultiplexWindowId,
  name: string
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(session => ({
    ...session,
    windows: session.windows.map(w =>
      w.id === windowId ? { ...w, name } : w
    ),
  }));
  return ok({ ...state, sessions }, 'MUX_WINDOW_RENAMED', `Window renamed to '${name}'`);
}

/**
 * Switch to the next window in the active session.
 */
export function nextWindow(state: MultiplexerState): EditorResult<MultiplexerState> {
  const session = getActiveSession(state);
  if (!session || session.windows.length === 0) {
    return err('MUX_NO_WINDOWS', 'No windows available');
  }
  const nextIdx = (session.activeWindowIndex + 1) % session.windows.length;
  return switchToWindow(state, nextIdx);
}

/**
 * Switch to the previous window in the active session.
 */
export function previousWindow(state: MultiplexerState): EditorResult<MultiplexerState> {
  const session = getActiveSession(state);
  if (!session || session.windows.length === 0) {
    return err('MUX_NO_WINDOWS', 'No windows available');
  }
  const prevIdx = session.activeWindowIndex <= 0
    ? session.windows.length - 1
    : session.activeWindowIndex - 1;
  return switchToWindow(state, prevIdx);
}

/**
 * Switch to a window by index.
 */
export function switchToWindow(
  state: MultiplexerState,
  index: number
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(s => {
    if (s.id !== state.activeSessionId) return s;
    if (index < 0 || index >= s.windows.length) return s;
    return { ...s, activeWindowIndex: index };
  });
  return ok({ ...state, sessions }, 'MUX_WINDOW_SWITCHED', `Switched to window ${index}`);
}

/**
 * Move a window to a new index position.
 */
export function moveWindow(
  state: MultiplexerState,
  windowId: MultiplexWindowId,
  targetIndex: number
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(session => {
    const idx = session.windows.findIndex(w => w.id === windowId);
    if (idx < 0) return session;

    const windows = [...session.windows];
    const [removed] = windows.splice(idx, 1);
    const insertIdx = clamp(targetIndex, 0, windows.length);
    windows.splice(insertIdx, 0, removed!);

    return { ...session, windows, activeWindowIndex: insertIdx };
  });
  return ok({ ...state, sessions }, 'MUX_WINDOW_MOVED', `Window moved to index ${targetIndex}`);
}

/**
 * Get the active window of the active session.
 */
export function getActiveWindow(state: MultiplexerState): MultiplexWindow | undefined {
  const session = getActiveSession(state);
  if (!session || session.activeWindowIndex < 0 || session.activeWindowIndex >= session.windows.length) {
    return undefined;
  }
  return session.windows[session.activeWindowIndex];
}

/**
 * List all windows in a session.
 */
export function listWindows(
  state: MultiplexerState,
  sessionId: MultiplexSessionId
): MultiplexWindow[] {
  const session = state.sessions.find(s => s.id === sessionId);
  return session?.windows ?? [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 PANE MANAGEMENT — split, remove, activate, navigate, swap, zoom, resize
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Split a pane horizontally, creating a new pane below.
 */
export function splitPaneHorizontal(
  state: MultiplexerState,
  paneId: MultiplexPaneId,
  terminalId: TerminalId
): EditorResult<MultiplexerState> {
  return splitPaneImpl(state, paneId, terminalId, 'horizontal');
}

/**
 * Split a pane vertically, creating a new pane to the right.
 */
export function splitPaneVertical(
  state: MultiplexerState,
  paneId: MultiplexPaneId,
  terminalId: TerminalId
): EditorResult<MultiplexerState> {
  return splitPaneImpl(state, paneId, terminalId, 'vertical');
}

/**
 * Remove a pane.
 */
export function removePane(
  state: MultiplexerState,
  paneId: MultiplexPaneId
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(session => ({
    ...session,
    windows: session.windows.map(window => {
      const idx = window.panes.findIndex(p => p.id === paneId);
      if (idx < 0) return window;

      const panes = window.panes.filter(p => p.id !== paneId);
      if (panes.length === 0) return window;

      // Activate the next pane if removing the active one
      let activePaneId = window.activePaneId;
      if (activePaneId === paneId) {
        activePaneId = panes[Math.min(idx, panes.length - 1)]!.id;
      }
      const updatedPanes = panes.map(p => ({
        ...p,
        isActive: p.id === activePaneId,
      }));

      return { ...window, panes: updatedPanes, activePaneId };
    }),
  }));
  return ok({ ...state, sessions }, 'MUX_PANE_REMOVED', `Pane '${paneId}' removed`);
}

/**
 * Activate a pane.
 */
export function activatePane(
  state: MultiplexerState,
  paneId: MultiplexPaneId
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(session => ({
    ...session,
    windows: session.windows.map(window => {
      if (!window.panes.some(p => p.id === paneId)) return window;
      const panes = window.panes.map(p => ({
        ...p,
        isActive: p.id === paneId,
      }));
      return { ...window, panes, activePaneId: paneId };
    }),
  }));
  return ok({ ...state, sessions }, 'MUX_PANE_ACTIVATED', `Pane '${paneId}' activated`);
}

/**
 * Switch to the next pane in the active window.
 */
export function nextPane(state: MultiplexerState): EditorResult<MultiplexerState> {
  const window = getActiveWindow(state);
  if (!window || window.panes.length === 0) {
    return err('MUX_NO_PANES', 'No panes available');
  }
  const currentIdx = window.panes.findIndex(p => p.id === window.activePaneId);
  const nextIdx = (currentIdx + 1) % window.panes.length;
  return activatePane(state, window.panes[nextIdx]!.id);
}

/**
 * Switch to the previous pane in the active window.
 */
export function previousPane(state: MultiplexerState): EditorResult<MultiplexerState> {
  const window = getActiveWindow(state);
  if (!window || window.panes.length === 0) {
    return err('MUX_NO_PANES', 'No panes available');
  }
  const currentIdx = window.panes.findIndex(p => p.id === window.activePaneId);
  const prevIdx = currentIdx <= 0 ? window.panes.length - 1 : currentIdx - 1;
  return activatePane(state, window.panes[prevIdx]!.id);
}

/**
 * Swap two panes.
 */
export function swapPanes(
  state: MultiplexerState,
  paneA: MultiplexPaneId,
  paneB: MultiplexPaneId
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(session => ({
    ...session,
    windows: session.windows.map(window => {
      const idxA = window.panes.findIndex(p => p.id === paneA);
      const idxB = window.panes.findIndex(p => p.id === paneB);
      if (idxA < 0 || idxB < 0) return window;

      const panes = [...window.panes];
      const a = panes[idxA]!;
      const b = panes[idxB]!;

      // Swap positions and sizes
      panes[idxA] = { ...a, position: b.position, size: b.size };
      panes[idxB] = { ...b, position: a.position, size: a.size };

      return { ...window, panes };
    }),
  }));
  return ok({ ...state, sessions }, 'MUX_PANES_SWAPPED', `Panes swapped`);
}

/**
 * Toggle zoom (fullscreen) on a pane.
 */
export function zoomPane(
  state: MultiplexerState,
  paneId: MultiplexPaneId
): EditorResult<MultiplexerState> {
  // When zoomed, we hide all other panes by setting their size to 0
  const sessions = state.sessions.map(session => ({
    ...session,
    windows: session.windows.map(window => {
      if (!window.panes.some(p => p.id === paneId)) return window;
      const panes = window.panes.map(p => ({
        ...p,
        isActive: p.id === paneId,
        isBorderVisible: p.id === paneId,
      }));
      return { ...window, panes, activePaneId: paneId };
    }),
  }));
  return ok({ ...state, sessions }, 'MUX_PANE_ZOOMED', `Pane '${paneId}' zoomed`);
}

/**
 * Unzoom (restore normal layout).
 */
export function unzoomPane(state: MultiplexerState): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(session => ({
    ...session,
    windows: session.windows.map(window => ({
      ...window,
      panes: window.panes.map(p => ({ ...p, isBorderVisible: true })),
    })),
  }));
  return ok({ ...state, sessions }, 'MUX_PANE_UNZOOMED', 'Pane unzoomed');
}

/**
 * Resize a pane in a given direction by an amount.
 */
export function resizePane(
  state: MultiplexerState,
  paneId: MultiplexPaneId,
  direction: 'up' | 'down' | 'left' | 'right',
  amount: number
): EditorResult<MultiplexerState> {
  const sessions = state.sessions.map(session => ({
    ...session,
    windows: session.windows.map(window => {
      const idx = window.panes.findIndex(p => p.id === paneId);
      if (idx < 0) return window;

      const pane = window.panes[idx]!;
      let newSize: PaneSize;

      switch (direction) {
        case 'up':
          newSize = { ...pane.size, height: Math.max(1, pane.size.height - amount) };
          break;
        case 'down':
          newSize = { ...pane.size, height: pane.size.height + amount };
          break;
        case 'left':
          newSize = { ...pane.size, width: Math.max(1, pane.size.width - amount) };
          break;
        case 'right':
          newSize = { ...pane.size, width: pane.size.width + amount };
          break;
      }

      const panes = [...window.panes];
      panes[idx] = { ...pane, size: newSize };
      return { ...window, panes };
    }),
  }));
  return ok({ ...state, sessions }, 'MUX_PANE_RESIZED', `Pane resized ${direction} by ${amount}`);
}

/**
 * Get the active pane.
 */
export function getActivePane(state: MultiplexerState): MultiplexPane | undefined {
  const window = getActiveWindow(state);
  if (!window) return undefined;
  return window.panes.find(p => p.id === window.activePaneId);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 PANE LAYOUT — applyLayout, compute layouts, customLayout, rebalanceLayout
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Apply a layout to panes, computing positions and sizes.
 */
export function applyLayout(
  panes: MultiplexPane[],
  layout: PaneLayout,
  width: number,
  height: number
): MultiplexPane[] {
  if (panes.length === 0) return panes;

  switch (layout) {
    case 'tiled': return applyTiledLayout(panes, width, height);
    case 'even-horizontal': return applyEvenHorizontal(panes, width, height);
    case 'even-vertical': return applyEvenVertical(panes, width, height);
    case 'main-horizontal': return applyMainHorizontal(panes, width, height);
    case 'main-vertical': return applyMainVertical(panes, width, height);
    case 'custom': return panes; // Custom keeps existing positions
  }
}

/**
 * Compute a tiled layout (grid-like arrangement).
 */
export function computeTiledLayout(
  paneCount: number,
  width: number,
  height: number
): Array<{ position: PanePosition; size: PaneSize }> {
  if (paneCount === 0) return [];
  if (paneCount === 1) {
    return [{ position: { row: 0, col: 0 }, size: { width, height } }];
  }

  const cols = Math.ceil(Math.sqrt(paneCount));
  const rows = Math.ceil(paneCount / cols);
  const cellWidth = Math.floor(width / cols);
  const cellHeight = Math.floor(height / rows);

  const result: Array<{ position: PanePosition; size: PaneSize }> = [];
  for (let i = 0; i < paneCount; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    result.push({
      position: { row: row * cellHeight, col: col * cellWidth },
      size: { width: cellWidth, height: cellHeight },
    });
  }
  return result;
}

/**
 * Even horizontal layout: equal-width columns.
 */
export function computeEvenHorizontal(
  paneCount: number,
  width: number,
  height: number
): Array<{ position: PanePosition; size: PaneSize }> {
  if (paneCount === 0) return [];
  const cellWidth = Math.floor(width / paneCount);
  return Array.from({ length: paneCount }, (_, i) => ({
    position: { row: 0, col: i * cellWidth },
    size: { width: cellWidth, height },
  }));
}

/**
 * Even vertical layout: equal-height rows.
 */
export function computeEvenVertical(
  paneCount: number,
  width: number,
  height: number
): Array<{ position: PanePosition; size: PaneSize }> {
  if (paneCount === 0) return [];
  const cellHeight = Math.floor(height / paneCount);
  return Array.from({ length: paneCount }, (_, i) => ({
    position: { row: i * cellHeight, col: 0 },
    size: { width, height: cellHeight },
  }));
}

/**
 * Main-horizontal layout: large pane on top, others split below.
 */
export function computeMainHorizontal(
  paneCount: number,
  width: number,
  height: number,
  mainRatio?: number
): Array<{ position: PanePosition; size: PaneSize }> {
  if (paneCount === 0) return [];
  if (paneCount === 1) {
    return [{ position: { row: 0, col: 0 }, size: { width, height } }];
  }

  const ratio = mainRatio ?? 0.6;
  const mainHeight = Math.floor(height * ratio);
  const subHeight = height - mainHeight;
  const subCount = paneCount - 1;
  const subWidth = Math.floor(width / subCount);

  const result: Array<{ position: PanePosition; size: PaneSize }> = [
    { position: { row: 0, col: 0 }, size: { width, height: mainHeight } },
  ];

  for (let i = 0; i < subCount; i++) {
    result.push({
      position: { row: mainHeight, col: i * subWidth },
      size: { width: subWidth, height: subHeight },
    });
  }
  return result;
}

/**
 * Main-vertical layout: large pane on left, others stacked right.
 */
export function computeMainVertical(
  paneCount: number,
  width: number,
  height: number,
  mainRatio?: number
): Array<{ position: PanePosition; size: PaneSize }> {
  if (paneCount === 0) return [];
  if (paneCount === 1) {
    return [{ position: { row: 0, col: 0 }, size: { width, height } }];
  }

  const ratio = mainRatio ?? 0.6;
  const mainWidth = Math.floor(width * ratio);
  const subWidth = width - mainWidth;
  const subCount = paneCount - 1;
  const subHeight = Math.floor(height / subCount);

  const result: Array<{ position: PanePosition; size: PaneSize }> = [
    { position: { row: 0, col: 0 }, size: { width: mainWidth, height } },
  ];

  for (let i = 0; i < subCount; i++) {
    result.push({
      position: { row: i * subHeight, col: mainWidth },
      size: { width: subWidth, height: subHeight },
    });
  }
  return result;
}

/**
 * Apply custom positions to panes.
 */
export function customLayout(
  panes: MultiplexPane[],
  positions: Array<{ position: PanePosition; size: PaneSize }>
): MultiplexPane[] {
  return panes.map((pane, i) => {
    if (i >= positions.length) return pane;
    const pos = positions[i]!;
    return { ...pane, position: pos.position, size: pos.size };
  });
}

/**
 * Rebalance layout after adding or removing a pane.
 */
export function rebalanceLayout(
  panes: MultiplexPane[],
  layout: PaneLayout,
  width: number,
  height: number
): MultiplexPane[] {
  return applyLayout(panes, layout, width, height);
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function splitPaneImpl(
  state: MultiplexerState,
  paneId: MultiplexPaneId,
  terminalId: TerminalId,
  direction: 'horizontal' | 'vertical'
): EditorResult<MultiplexerState> {
  const newPaneId = asMultiplexPaneId(generateId('mux-pane'));

  const sessions = state.sessions.map(session => ({
    ...session,
    windows: session.windows.map(window => {
      const idx = window.panes.findIndex(p => p.id === paneId);
      if (idx < 0) return window;

      const existing = window.panes[idx]!;
      let newPaneSize: PaneSize;
      let updatedExisting: MultiplexPane;

      if (direction === 'horizontal') {
        const halfHeight = Math.floor(existing.size.height / 2);
        updatedExisting = {
          ...existing,
          size: { ...existing.size, height: halfHeight },
        };
        newPaneSize = {
          width: existing.size.width,
          height: existing.size.height - halfHeight,
        };
      } else {
        const halfWidth = Math.floor(existing.size.width / 2);
        updatedExisting = {
          ...existing,
          size: { ...existing.size, width: halfWidth },
        };
        newPaneSize = {
          width: existing.size.width - halfWidth,
          height: existing.size.height,
        };
      }

      const newPosition: PanePosition = direction === 'horizontal'
        ? { row: existing.position.row + updatedExisting.size.height, col: existing.position.col }
        : { row: existing.position.row, col: existing.position.col + updatedExisting.size.width };

      const newPane: MultiplexPane = {
        id: newPaneId,
        terminalId,
        position: newPosition,
        size: newPaneSize,
        isActive: false,
        isBorderVisible: true,
      };

      const panes = [...window.panes];
      panes[idx] = updatedExisting;
      panes.splice(idx + 1, 0, newPane);

      return { ...window, panes };
    }),
  }));

  return ok(
    { ...state, sessions },
    'MUX_PANE_SPLIT',
    `Pane split ${direction}ly`
  );
}

function applyTiledLayout(panes: MultiplexPane[], width: number, height: number): MultiplexPane[] {
  const positions = computeTiledLayout(panes.length, width, height);
  return panes.map((pane, i) => ({
    ...pane,
    position: positions[i]!.position,
    size: positions[i]!.size,
  }));
}

function applyEvenHorizontal(panes: MultiplexPane[], width: number, height: number): MultiplexPane[] {
  const positions = computeEvenHorizontal(panes.length, width, height);
  return panes.map((pane, i) => ({
    ...pane,
    position: positions[i]!.position,
    size: positions[i]!.size,
  }));
}

function applyEvenVertical(panes: MultiplexPane[], width: number, height: number): MultiplexPane[] {
  const positions = computeEvenVertical(panes.length, width, height);
  return panes.map((pane, i) => ({
    ...pane,
    position: positions[i]!.position,
    size: positions[i]!.size,
  }));
}

function applyMainHorizontal(panes: MultiplexPane[], width: number, height: number): MultiplexPane[] {
  const positions = computeMainHorizontal(panes.length, width, height);
  return panes.map((pane, i) => ({
    ...pane,
    position: positions[i]!.position,
    size: positions[i]!.size,
  }));
}

function applyMainVertical(panes: MultiplexPane[], width: number, height: number): MultiplexPane[] {
  const positions = computeMainVertical(panes.length, width, height);
  return panes.map((pane, i) => ({
    ...pane,
    position: positions[i]!.position,
    size: positions[i]!.size,
  }));
}
